import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import type { Ticket, NewTicket, TicketComment, NewTicketComment, TicketSLAConfig, TicketPriority, SLA_PRIORITY_MULTIPLIERS } from '../types';
import { triggerWhatsAppAutomation } from '../utils/whatsapp';
import { getCurrentUserOrganizationId } from './referenceDataCache';
import { eventBus } from '../events/eventBus';

// Query select estesa con tutte le relazioni
const TICKET_SELECT_QUERY = `
  *,
  client:clients (
    id,
    business_name,
    email,
    phone
  ),
  assignee:user_profiles!tickets_assigned_to_fkey (
    id,
    full_name,
    email,
    profile_image_url
  ),
  ticket_type:ticket_types (
    id,
    name,
    description,
    color,
    icon,
    default_response_hours,
    default_resolution_hours,
    is_billable
  ),
  deal:deals (
    id,
    title
  ),
  deal_service:deal_services (
    id,
    is_hours_package,
    hours_package,
    hours_used,
    hourly_rate,
    hours_alert_threshold,
    service:services (
      id,
      title
    )
  ),
  project:projects (
    id,
    name
  )
`;

interface TicketFilters {
  status?: string[];
  priority?: string[];
  ticketTypeId?: string;
  assignedTo?: string;
  clientId?: string;
  dealId?: string;
  dealServiceId?: string; // NUOVO: filtra per deal_service (monte ore)
  projectId?: string;
  slaBreached?: boolean;
}

interface TicketStore {
  tickets: Ticket[];
  selectedTicket: Ticket | null;
  ticketComments: TicketComment[];
  loading: boolean;
  error: string | null;
  filters: TicketFilters;

  // Fetch
  fetchTickets: (filters?: TicketFilters) => Promise<void>;
  fetchClientTickets: (clientId: string) => Promise<void>;
  fetchDealTickets: (dealId: string) => Promise<void>;
  fetchProjectTickets: (projectId: string) => Promise<void>;
  fetchTicketById: (id: string) => Promise<void>;
  fetchTicketComments: (ticketId: string) => Promise<void>;

  // CRUD
  createTicket: (ticket: NewTicket) => Promise<Ticket | null>;
  updateTicket: (id: string, ticket: Partial<Ticket>) => Promise<Ticket | null>;
  deleteTicket: (id: string) => Promise<boolean>;
  createTicketComment: (comment: NewTicketComment) => Promise<TicketComment | null>;

  // SLA
  calculateSLADates: (ticket: Partial<Ticket>) => Promise<{ sla_response_due: string; sla_resolution_due: string } | null>;
  recordFirstResponse: (ticketId: string) => Promise<void>;
  recordResolution: (ticketId: string) => Promise<void>;

  // Utility
  setFilters: (filters: TicketFilters) => void;
  clearFilters: () => void;
  clearSelectedTicket: () => void;
}

// Flag per evitare fetch simultanei
let isFetchingTickets = false;

// Costanti moltiplicatori priorità
const PRIORITY_MULTIPLIERS: Record<TicketPriority, number> = {
  low: 2.0,
  medium: 1.0,
  high: 0.5,
  urgent: 0.25
};

export const useTicketStore = create<TicketStore>((set, get) => ({
  tickets: [],
  selectedTicket: null,
  ticketComments: [],
  loading: false,
  error: null,
  filters: {},

  fetchTickets: async (filters?: TicketFilters) => {
    if (isFetchingTickets) return;
    isFetchingTickets = true;

    set({ loading: true, error: null });
    try {
      const organizationId = await getCurrentUserOrganizationId();
      if (!organizationId) {
        throw new Error('Organization ID not found');
      }

      let query = supabase
        .from('tickets')
        .select(TICKET_SELECT_QUERY)
        .eq('organization_id', organizationId);

      // Applica filtri
      const activeFilters = filters || get().filters;

      if (activeFilters.status && activeFilters.status.length > 0) {
        query = query.in('status', activeFilters.status);
      }
      if (activeFilters.priority && activeFilters.priority.length > 0) {
        query = query.in('priority', activeFilters.priority);
      }
      if (activeFilters.ticketTypeId) {
        query = query.eq('ticket_type_id', activeFilters.ticketTypeId);
      }
      if (activeFilters.assignedTo) {
        query = query.eq('assigned_to', activeFilters.assignedTo);
      }
      if (activeFilters.clientId) {
        query = query.eq('client_id', activeFilters.clientId);
      }
      if (activeFilters.dealId) {
        query = query.eq('deal_id', activeFilters.dealId);
      }
      if (activeFilters.dealServiceId) {
        query = query.eq('deal_service_id', activeFilters.dealServiceId);
      }
      if (activeFilters.projectId) {
        query = query.eq('project_id', activeFilters.projectId);
      }
      if (activeFilters.slaBreached !== undefined) {
        if (activeFilters.slaBreached) {
          query = query.or('sla_response_breached.eq.true,sla_resolution_breached.eq.true');
        }
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      set({ tickets: data || [], loading: false });
    } catch (error) {
      console.error('Error fetching tickets:', error);
      set({ error: friendlyDbError(error), loading: false });
    } finally {
      isFetchingTickets = false;
    }
  },

  fetchClientTickets: async (clientId: string) => {
    set({ loading: true, error: null });
    try {
      const organizationId = await getCurrentUserOrganizationId();
      if (!organizationId) {
        throw new Error('Organization ID not found');
      }

      const { data, error } = await supabase
        .from('tickets')
        .select(TICKET_SELECT_QUERY)
        .eq('client_id', clientId)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ tickets: data || [], loading: false });
    } catch (error) {
      console.error('Error fetching client tickets:', error);
      set({ error: friendlyDbError(error), loading: false });
    }
  },

  fetchDealTickets: async (dealId: string) => {
    set({ loading: true, error: null });
    try {
      const organizationId = await getCurrentUserOrganizationId();
      if (!organizationId) {
        throw new Error('Organization ID not found');
      }

      const { data, error } = await supabase
        .from('tickets')
        .select(TICKET_SELECT_QUERY)
        .eq('deal_id', dealId)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ tickets: data || [], loading: false });
    } catch (error) {
      console.error('Error fetching deal tickets:', error);
      set({ error: friendlyDbError(error), loading: false });
    }
  },

  fetchProjectTickets: async (projectId: string) => {
    set({ loading: true, error: null });
    try {
      const organizationId = await getCurrentUserOrganizationId();
      if (!organizationId) {
        throw new Error('Organization ID not found');
      }

      const { data, error } = await supabase
        .from('tickets')
        .select(TICKET_SELECT_QUERY)
        .eq('project_id', projectId)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ tickets: data || [], loading: false });
    } catch (error) {
      console.error('Error fetching project tickets:', error);
      set({ error: friendlyDbError(error), loading: false });
    }
  },

  fetchTicketById: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const organizationId = await getCurrentUserOrganizationId();
      if (!organizationId) {
        throw new Error('Organization ID not found');
      }

      const { data, error } = await supabase
        .from('tickets')
        .select(TICKET_SELECT_QUERY)
        .eq('id', id)
        .eq('organization_id', organizationId)
        .single();

      if (error) throw error;
      set({ selectedTicket: data, loading: false });
    } catch (error) {
      console.error('Error fetching ticket:', error);
      set({ error: friendlyDbError(error), loading: false });
    }
  },

  fetchTicketComments: async (ticketId: string) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('ticket_comments')
        .select(`
          *,
          author:user_profiles!ticket_comments_created_by_fkey (
            id,
            full_name,
            email,
            profile_image_url
          )
        `)
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      set({ ticketComments: data || [], loading: false });
    } catch (error) {
      console.error('Error fetching ticket comments:', error);
      set({ error: friendlyDbError(error), loading: false });
    }
  },

  createTicket: async (ticket: NewTicket) => {
    try {
      const organizationId = await getCurrentUserOrganizationId();
      if (!organizationId) {
        throw new Error('Organization ID not found');
      }

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      // Calcola SLA dates se possibile
      let slaData: { sla_response_due: string; sla_resolution_due: string } | null = null;
      if (ticket.ticket_type_id || ticket.deal_id) {
        slaData = await get().calculateSLADates(ticket);
      }

      const { data, error } = await supabase
        .from('tickets')
        .insert({
          ...ticket,
          organization_id: organizationId,
          created_by: userData.user.id,
          sla_response_due: slaData?.sla_response_due,
          sla_resolution_due: slaData?.sla_resolution_due,
          sla_response_breached: false,
          sla_resolution_breached: false,
          total_hours_worked: 0
        })
        .select(TICKET_SELECT_QUERY)
        .single();

      if (error) throw error;

      // Trigger WhatsApp automation for ticket creation
      triggerWhatsAppAutomation({
        entityType: 'ticket',
        triggerEvent: 'create',
        entityData: data
      }).catch(err => console.error('Error triggering WhatsApp automation:', err));

      // Emit workflow trigger event
      try {
        eventBus.emitEvent('ticket:created' as any, {
          ticketId: data.id,
          ticketTitle: data.title,
          ticketStatus: data.status,
          ticketPriority: data.priority,
          clientId: data.client_id,
          assignedTo: data.assigned_to,
          projectId: data.project_id,
          dealId: data.deal_id,
        });
      } catch (e) { console.warn('[TicketStore] Failed to emit ticket:created event:', e); }

      set((state) => ({
        tickets: [data, ...state.tickets]
      }));

      return data;
    } catch (error) {
      console.error('Error creating ticket:', error);
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  updateTicket: async (id: string, ticket: Partial<Ticket>) => {
    try {
      const organizationId = await getCurrentUserOrganizationId();
      if (!organizationId) {
        throw new Error('Organization ID not found');
      }

      // Get current ticket data to detect status changes
      const { data: current } = await supabase
        .from('tickets')
        .select('status, first_response_at, resolved_at')
        .eq('id', id)
        .eq('organization_id', organizationId)
        .single();

      const oldStatus = current?.status;
      const isStatusChange = ticket.status && ticket.status !== oldStatus;

      // Se lo stato cambia a "resolved" o "closed", registra resolved_at
      let additionalUpdates: Partial<Ticket> = {};
      if (isStatusChange) {
        if ((ticket.status === 'resolved' || ticket.status === 'closed') && !current?.resolved_at) {
          additionalUpdates.resolved_at = new Date().toISOString();
        }
      }

      const { data, error } = await supabase
        .from('tickets')
        .update({
          ...ticket,
          ...additionalUpdates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('organization_id', organizationId)
        .select(TICKET_SELECT_QUERY)
        .single();

      if (error) throw error;

      // Trigger appropriate WhatsApp automation
      if (isStatusChange) {
        triggerWhatsAppAutomation({
          entityType: 'ticket',
          triggerEvent: 'status_change',
          entityData: data,
          oldStatus
        }).catch(err => console.error('Error triggering WhatsApp automation:', err));
      } else {
        triggerWhatsAppAutomation({
          entityType: 'ticket',
          triggerEvent: 'update',
          entityData: data
        }).catch(err => console.error('Error triggering WhatsApp automation:', err));
      }

      set((state) => ({
        tickets: state.tickets.map(t => t.id === id ? { ...t, ...data } : t),
        selectedTicket: state.selectedTicket?.id === id ? { ...state.selectedTicket, ...data } : state.selectedTicket
      }));

      return data;
    } catch (error) {
      console.error('Error updating ticket:', error);
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  deleteTicket: async (id: string) => {
    try {
      const organizationId = await getCurrentUserOrganizationId();
      if (!organizationId) {
        throw new Error('Organization ID not found');
      }

      // Get the ticket before deleting for the trigger
      const { data: ticketData } = await supabase
        .from('tickets')
        .select(`
          *,
          client:clients (
            id,
            business_name
          )
        `)
        .eq('id', id)
        .eq('organization_id', organizationId)
        .single();

      const { error } = await supabase
        .from('tickets')
        .delete()
        .eq('id', id)
        .eq('organization_id', organizationId);

      if (error) throw error;

      // Trigger WhatsApp automation for deletion
      if (ticketData) {
        triggerWhatsAppAutomation({
          entityType: 'ticket',
          triggerEvent: 'delete',
          entityData: ticketData
        }).catch(err => console.error('Error triggering WhatsApp automation:', err));
      }

      set((state) => ({
        tickets: state.tickets.filter(t => t.id !== id),
        selectedTicket: state.selectedTicket?.id === id ? null : state.selectedTicket
      }));

      return true;
    } catch (error) {
      console.error('Error deleting ticket:', error);
      set({ error: friendlyDbError(error) });
      return false;
    }
  },

  createTicketComment: async (comment: NewTicketComment) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('ticket_comments')
        .insert({
          ...comment,
          created_by: userData.user.id
        })
        .select(`
          *,
          author:user_profiles!ticket_comments_created_by_fkey (
            id,
            full_name,
            email,
            profile_image_url
          )
        `)
        .single();

      if (error) throw error;

      // Se è il primo commento (prima risposta), aggiorna first_response_at
      const { selectedTicket } = get();
      if (selectedTicket && !selectedTicket.first_response_at) {
        await get().recordFirstResponse(selectedTicket.id);
      }

      set((state) => ({
        ticketComments: [...state.ticketComments, data]
      }));

      return data;
    } catch (error) {
      console.error('Error creating ticket comment:', error);
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  calculateSLADates: async (ticket: Partial<Ticket>) => {
    try {
      const organizationId = await getCurrentUserOrganizationId();
      if (!organizationId) return null;

      let responseHours = 24; // Default
      let resolutionHours = 72; // Default
      let priorityMultiplier = PRIORITY_MULTIPLIERS[ticket.priority || 'medium'];

      // 1. Cerca SLA config per deal specifico + ticket_type
      if (ticket.deal_id && ticket.ticket_type_id) {
        const { data: dealTypeConfig } = await supabase
          .from('ticket_sla_configs')
          .select('*')
          .eq('deal_id', ticket.deal_id)
          .eq('ticket_type_id', ticket.ticket_type_id)
          .eq('is_active', true)
          .single();

        if (dealTypeConfig) {
          responseHours = dealTypeConfig.response_hours;
          resolutionHours = dealTypeConfig.resolution_hours;
          // Usa i moltiplicatori custom se presenti
          const priorityField = `priority_multiplier_${ticket.priority || 'medium'}` as keyof typeof dealTypeConfig;
          if (dealTypeConfig[priorityField] !== undefined) {
            priorityMultiplier = Number(dealTypeConfig[priorityField]);
          }
        }
      }

      // 2. Fallback: SLA config solo per deal
      if (responseHours === 24 && ticket.deal_id) {
        const { data: dealConfig } = await supabase
          .from('ticket_sla_configs')
          .select('*')
          .eq('deal_id', ticket.deal_id)
          .is('ticket_type_id', null)
          .eq('is_active', true)
          .single();

        if (dealConfig) {
          responseHours = dealConfig.response_hours;
          resolutionHours = dealConfig.resolution_hours;
        }
      }

      // 3. Fallback: SLA config solo per ticket_type
      if (responseHours === 24 && ticket.ticket_type_id) {
        const { data: typeConfig } = await supabase
          .from('ticket_sla_configs')
          .select('*')
          .is('deal_id', null)
          .eq('ticket_type_id', ticket.ticket_type_id)
          .eq('is_active', true)
          .single();

        if (typeConfig) {
          responseHours = typeConfig.response_hours;
          resolutionHours = typeConfig.resolution_hours;
        }
      }

      // 4. Fallback: Default dal ticket_type
      if (responseHours === 24 && ticket.ticket_type_id) {
        const { data: ticketType } = await supabase
          .from('ticket_types')
          .select('default_response_hours, default_resolution_hours')
          .eq('id', ticket.ticket_type_id)
          .single();

        if (ticketType) {
          responseHours = ticketType.default_response_hours || 24;
          resolutionHours = ticketType.default_resolution_hours || 72;
        }
      }

      // Applica moltiplicatore priorità
      const effectiveResponseHours = responseHours * priorityMultiplier;
      const effectiveResolutionHours = resolutionHours * priorityMultiplier;

      const now = new Date();
      const sla_response_due = new Date(now.getTime() + effectiveResponseHours * 60 * 60 * 1000).toISOString();
      const sla_resolution_due = new Date(now.getTime() + effectiveResolutionHours * 60 * 60 * 1000).toISOString();

      return { sla_response_due, sla_resolution_due };
    } catch (error) {
      console.error('Error calculating SLA dates:', error);
      return null;
    }
  },

  recordFirstResponse: async (ticketId: string) => {
    try {
      const organizationId = await getCurrentUserOrganizationId();
      if (!organizationId) return;

      const now = new Date().toISOString();

      // Controlla se la risposta è in tempo
      const { data: ticket } = await supabase
        .from('tickets')
        .select('sla_response_due')
        .eq('id', ticketId)
        .single();

      const breached = ticket?.sla_response_due ? new Date(now) > new Date(ticket.sla_response_due) : false;

      await supabase
        .from('tickets')
        .update({
          first_response_at: now,
          sla_response_breached: breached
        })
        .eq('id', ticketId)
        .eq('organization_id', organizationId);

      // Aggiorna stato locale
      set((state) => ({
        selectedTicket: state.selectedTicket?.id === ticketId
          ? { ...state.selectedTicket, first_response_at: now, sla_response_breached: breached }
          : state.selectedTicket,
        tickets: state.tickets.map(t =>
          t.id === ticketId ? { ...t, first_response_at: now, sla_response_breached: breached } : t
        )
      }));
    } catch (error) {
      console.error('Error recording first response:', error);
    }
  },

  recordResolution: async (ticketId: string) => {
    try {
      const organizationId = await getCurrentUserOrganizationId();
      if (!organizationId) return;

      const now = new Date().toISOString();

      // Controlla se la risoluzione è in tempo
      const { data: ticket } = await supabase
        .from('tickets')
        .select('sla_resolution_due')
        .eq('id', ticketId)
        .single();

      const breached = ticket?.sla_resolution_due ? new Date(now) > new Date(ticket.sla_resolution_due) : false;

      await supabase
        .from('tickets')
        .update({
          resolved_at: now,
          sla_resolution_breached: breached
        })
        .eq('id', ticketId)
        .eq('organization_id', organizationId);

      // Aggiorna stato locale
      set((state) => ({
        selectedTicket: state.selectedTicket?.id === ticketId
          ? { ...state.selectedTicket, resolved_at: now, sla_resolution_breached: breached }
          : state.selectedTicket,
        tickets: state.tickets.map(t =>
          t.id === ticketId ? { ...t, resolved_at: now, sla_resolution_breached: breached } : t
        )
      }));
    } catch (error) {
      console.error('Error recording resolution:', error);
    }
  },

  setFilters: (filters: TicketFilters) => {
    set({ filters });
  },

  clearFilters: () => {
    set({ filters: {} });
  },

  clearSelectedTicket: () => {
    set({ selectedTicket: null, ticketComments: [] });
  }
}));
